import {
  GoogleAuthProvider,
  signInWithPopup,
  User,
  UserCredential,
} from 'firebase/auth';
import { setDoc, Timestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import { userProfileDocument } from '../lib/paths';
import { ensureUserProfile } from './ensureUserProfile';
import { withTimeout } from './withTimeout';
import { TERMS_VERSION } from '../pages/TermsOfService';
import { PRIVACY_VERSION } from '../pages/PrivacyPolicy';
import { logger } from './logger';

// Unlike the password calls, this budget covers the user reading the account
// chooser, not just network round-trips — so minutes, not seconds. It bounds
// the popup exchange only (a stuck auth iframe or App Check token); the
// profile and consent writes after it run unbounded, the same shape as the
// password paths, where a connection lost mid-write can still hold the
// button past this bound.
const GOOGLE_SIGN_IN_TIMEOUT_MS = 120000;
const TIMEOUT_MESSAGE =
  'Połączenie z serwerem trwa zbyt długo. Sprawdź połączenie z internetem i spróbuj ponownie.';

/** True when the account can sign in with an email and password. */
export const hasPasswordProvider = (user: User): boolean =>
  user.providerData.some(p => p.providerId === 'password');

/**
 * The user closed the popup or started a second one — a change of mind, not
 * a failure, so callers should show nothing rather than an error.
 */
export function isPopupCancelledError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('code' in err)) {
    return false;
  }
  const code = String((err as { code: unknown }).code);
  return (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request'
  );
}

/**
 * Signs in with Google and provisions a profile for first-time accounts.
 *
 * Popup rather than redirect: signInWithRedirect keeps its state in
 * third-party storage on the Firebase authDomain, which Safari and Chrome
 * now partition, so the redirect can come back to a session that no longer
 * remembers starting it. The popup keeps the whole exchange in one window —
 * and needs the authDomain in the CSP frame-src, because the SDK receives
 * the auth event through a hidden iframe on that domain.
 *
 * A freshly created profile gets the current terms recorded: the button
 * carries a disclosure that continuing means accepting them, so the click is
 * the affirmative act — the register checkbox's equivalent. Existing
 * profiles are left alone; MigrationHandler prompts anyone whose recorded
 * version is missing or stale.
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  const credential = await withTimeout(
    signInWithPopup(auth, new GoogleAuthProvider()),
    GOOGLE_SIGN_IN_TIMEOUT_MS,
    TIMEOUT_MESSAGE
  );
  const created = await ensureUserProfile(credential.user);
  if (created) {
    try {
      await setDoc(
        userProfileDocument(credential.user.uid),
        {
          termsAcceptedAt: Timestamp.now(),
          termsVersion: TERMS_VERSION,
          privacyAcceptedAt: Timestamp.now(),
          privacyVersion: PRIVACY_VERSION,
        },
        { merge: true }
      );
    } catch {
      // Never block sign-in on this — a profile without recorded terms is
      // exactly what the MigrationHandler modal exists to catch.
      logger.warn('Could not record terms acceptance', undefined, {
        component: 'googleSignIn',
        operation: 'recordTermsAcceptance',
      });
    }
  }
  return credential;
}
